// validationUtils.js
import dayjs from 'dayjs';

export const validateTableData = (tableData) => {
  const errors = [];
  const duplicateMap = new Map();

  // Date validation setup
  const today = dayjs();
  const ninetyDaysAgo = today.subtract(90, 'day');

  // First pass: collect all entries for duplicate checking
  tableData.forEach((row, index) => {
    const {
      BP_Code,
      To_Customer,
      Customer_Address,
      SKU,
      Posting_date,
      Document_Date,
      Transporter_Name,
      Vehicle_Number,
      LR_No,
      Invoice_No
    } = row;

    // Safely extract SKU code (handle different data types)
    let skuCode = '';
    if (SKU) {
      if (typeof SKU === 'string') {
        skuCode = SKU.split('-')[0].trim();
      } else if (typeof SKU === 'number') {
        skuCode = SKU.toString();
      } else {
        skuCode = String(SKU);
      }
    }

    // Create unique identifier that includes ALL fields
    const uniqueId = `${BP_Code}-${To_Customer}-${Customer_Address}-${skuCode}-${Posting_date}-${Document_Date}-${Transporter_Name}-${Vehicle_Number}-${LR_No}-${Invoice_No}`;

    if (!duplicateMap.has(uniqueId)) {
      duplicateMap.set(uniqueId, []);
    }
    duplicateMap.get(uniqueId).push({ index, Invoice_No, SKU: skuCode });
  });

  // Second pass: check for validation issues
  tableData.forEach((row, index) => {
    const {
      BP_Code,
      To_Customer,
      Customer_Address,
      SKU,
      Posting_date,
      Document_Date,
      Transporter_Name,
      Vehicle_Number,
      LR_No,
      Invoice_No
    } = row;

    // Safely extract SKU code
    let skuCode = '';
    if (SKU) {
      if (typeof SKU === 'string') {
        skuCode = SKU.split('-')[0].trim();
      } else if (typeof SKU === 'number') {
        skuCode = SKU.toString();
      } else {
        skuCode = String(SKU);
      }
    }

    const uniqueId = `${BP_Code}-${To_Customer}-${Customer_Address}-${skuCode}-${Posting_date}-${Document_Date}-${Transporter_Name}-${Vehicle_Number}-${LR_No}-${Invoice_No}`;

    // Check for exact duplicates (same everything including SKU and Invoice No)
    const similarEntries = duplicateMap.get(uniqueId);
    if (similarEntries && similarEntries.length > 1) {
      const otherEntries = similarEntries.filter(entry => entry.index !== index);

      if (otherEntries.length > 0) {
        errors.push({
          row: index + 1,
          message: `Duplicate entry detected. Same data exists in row ${otherEntries[0].index + 1} with same Invoice No: ${Invoice_No} and SKU: ${skuCode}`,
          type: 'duplicate',
          severity: 'error'
        });
      }
    }

    // Mandatory field validation
    const mandatoryFields = [
      { field: 'BP_Code', name: 'BP Code' },
      { field: 'To_Customer', name: 'To Customer' },
      { field: 'SKU', name: 'SKU' },
      { field: 'Posting_date', name: 'Posting Date' },
      { field: 'Document_Date', name: 'Document Date' },
      { field: 'Invoice_No', name: 'Invoice No' }
    ];

    mandatoryFields.forEach(({ field, name }) => {
      const value = row[field];
      if (!value || value.toString().trim() === '') {
        errors.push({
          row: index + 1,
          message: `${name} is required`,
          type: 'required',
          severity: 'error'
        });
      }
    });

    // Document Date validation (90-day window)
    if (Document_Date) {
      const docDate = dayjs(Document_Date);
      if (!docDate.isValid()) {
        errors.push({
          row: index + 1,
          field: 'Document_Date',
          message: 'Invalid date format',
          type: 'validation',
          severity: 'error'
        });
      } else if (docDate.isAfter(today, 'day')) {
        errors.push({
          row: index + 1,
          field: 'Document_Date',
          message: 'Document date cannot be in the future',
          type: 'validation',
          severity: 'error'
        });
      } else if (docDate.isBefore(ninetyDaysAgo, 'day')) {
        errors.push({
          row: index + 1,
          field: 'Document_Date',
          message: 'Document date cannot be more than 90 days in the past',
          type: 'validation',
          severity: 'error'
        });
      }
    }

    // Quantity validation - check SKU QUANTITY object
    const hasValidQuantity = () => {
      const skuQuantities = row["SKU QUANTITY"] || {};
      const quantities = Object.values(skuQuantities);

      if (quantities.length === 0) {
        return false;
      }

      return quantities.every(qty => {
        const quantity = parseFloat(qty);
        return !isNaN(quantity) && quantity > 0;
      });
    };

    if (!hasValidQuantity()) {
      errors.push({
        row: index + 1,
        message: 'Valid quantity is required for all SKUs',
        type: 'validation',
        severity: 'error'
      });
    }
  });

  return errors;
};

export const validateBulkTransferEntries = (apiItems) => {
  const errors = [];
  const duplicateMap = new Map();

  // Date validation setup
  const today = dayjs();
  const ninetyDaysAgo = today.subtract(90, 'day');

  // First pass: collect all entries for duplicate checking
  apiItems.forEach((apiItem, index) => {
    const header = apiItem.HEADER || {};
    const items = apiItem.ITEMS?.item || [];

    const {
      BP_Code,
      To_Customer,
      Posting_date,
      Document_Date,
      Transporter_Name,
      Vehicle_Number,
      LR_No,
      Invoice_No
    } = header;

    // For each item in this entry, create a unique identifier
    items.forEach((item, itemIndex) => {
      const sku = item.Material || item.Component || '';
      const quantity = item.Quantity || 0;

      // Skip empty items
      if (!sku || quantity <= 0) return;

      // Safely handle SKU value (could be number or string)
      const skuValue = typeof sku === 'string' ? sku : String(sku);

      // Create unique identifier that includes ALL fields
      const uniqueId = `${BP_Code}-${To_Customer}-${Posting_date}-${Document_Date}-${Transporter_Name}-${Vehicle_Number}-${LR_No}-${skuValue}-${Invoice_No}-${quantity}`;

      if (!duplicateMap.has(uniqueId)) {
        duplicateMap.set(uniqueId, []);
      }
      duplicateMap.get(uniqueId).push({
        index,
        itemIndex,
        Invoice_No,
        SKU: skuValue,
        Quantity: quantity
      });
    });
  });

  // Second pass: check for validation issues
  apiItems.forEach((apiItem, index) => {
    const header = apiItem.HEADER || {};
    const items = apiItem.ITEMS?.item || [];

    const {
      BP_Code,
      To_Customer,
      Posting_date,
      Document_Date,
      Transporter_Name,
      Vehicle_Number,
      LR_No,
      Invoice_No
    } = header;

    // Check if this entry has any items
    if (!items || items.length === 0) {
      errors.push({
        row: index + 1,
        message: 'No items found in this entry',
        type: 'validation',
        severity: 'error'
      });
      return; // Skip further validation for this entry
    }

    // Document Date validation (90-day window) - API level
    if (Document_Date) {
      const docDate = dayjs(Document_Date);
      if (!docDate.isValid()) {
        errors.push({
          row: index + 1,
          field: 'Document_Date',
          message: 'Invalid date format',
          type: 'validation',
          severity: 'error'
        });
      } else if (docDate.isAfter(today, 'day')) {
        errors.push({
          row: index + 1,
          field: 'Document_Date',
          message: 'Document date cannot be in the future',
          type: 'validation',
          severity: 'error'
        });
      } else if (docDate.isBefore(ninetyDaysAgo, 'day')) {
        errors.push({
          row: index + 1,
          field: 'Document_Date',
          message: 'Document date cannot be more than 90 days in the past',
          type: 'validation',
          severity: 'error'
        });
      }
    }

    // Check each item in this entry
    items.forEach((item, itemIndex) => {
      const sku = item.Material || item.Component || '';
      const quantity = item.Quantity || 0;

      // Skip validation for empty items (they'll be caught by quantity validation)
      if (!sku) return;

      // Safely handle SKU value
      const skuValue = typeof sku === 'string' ? sku : String(sku);

      // Create the same unique identifier
      const uniqueId = `${BP_Code}-${To_Customer}-${Posting_date}-${Document_Date}-${Transporter_Name}-${Vehicle_Number}-${LR_No}-${skuValue}-${Invoice_No}-${quantity}`;

      // Check for duplicate combinations
      const similarEntries = duplicateMap.get(uniqueId);
      if (similarEntries && similarEntries.length > 1) {
        const otherEntries = similarEntries.filter(entry =>
          entry.index !== index || entry.itemIndex !== itemIndex
        );

        if (otherEntries.length > 0) {
          errors.push({
            row: index + 1,
            message: `Duplicate entry detected. Same data exists in row ${otherEntries[0].index + 1} with same Invoice No: ${Invoice_No} and SKU: ${skuValue}`,
            type: 'duplicate',
            severity: 'error'
          });
        }
      }

      // Quantity validation for this item
      const quantityNum = typeof quantity === 'string' ? parseFloat(quantity) : quantity;
      if (isNaN(quantityNum) || quantityNum <= 0) {
        errors.push({
          row: index + 1,
          message: `Valid quantity is required for SKU: ${skuValue}`,
          type: 'validation',
          severity: 'error'
        });
      }
    });

    // Mandatory field validation (header level)
    const mandatoryFields = [
      { field: 'BP_Code', name: 'BP Code' },
      { field: 'To_Customer', name: 'To Customer' },
      { field: 'Posting_date', name: 'Posting Date' },
      { field: 'Document_Date', name: 'Document Date' },
      { field: 'Invoice_No', name: 'Invoice No' }
    ];

    mandatoryFields.forEach(({ field, name }) => {
      const value = header[field];
      if (!value || value.toString().trim() === '') {
        errors.push({
          row: index + 1,
          message: `${name} is required`,
          type: 'required',
          severity: 'error'
        });
      }
    });
  });

  return errors;
};

// Helper function to format validation errors for display
export const formatValidationErrors = (errors, maxDisplay = 5) => {
  if (errors.length === 0) return null;

  return (
    <div>
      <p>Please fix the following errors:</p>
      <ul>
        {errors.slice(0, maxDisplay).map((error, index) => (
          <li key={index}>Row {error.row}: {error.message}</li>
        ))}
        {errors.length > maxDisplay && (
          <li>...and {errors.length - maxDisplay} more errors</li>
        )}
      </ul>
    </div>
  );
};

// Export for backward compatibility
// eslint-disable-next-line import/no-anonymous-default-export
export default {
  validateTableData,
  validateBulkTransferEntries,
  formatValidationErrors
};
